import { FormHelperText, InputLabel, Select, SelectChangeEvent, SelectProps, Stack, StackProps } from "@mui/material";
import React, { useEffect } from "react";
import { DeleteField, GetFieldIsValidAndErrorMessage } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import useSelectValidator, { SelectValidatorProps } from "../../../Utils/Hooks/Validators/useSelectValidator";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

interface FormInputSelectProps<T> extends Omit<SelectProps<T>, "id" | "label" | "error" | "type" | "variant"> {
  label?: string;
  validationProps: SelectValidatorProps<T>;
  stackProps: StackProps;
}

const FormInputSelect = <T,>(props: FormInputSelectProps<T>) => {
  const { label, validationProps, children, onChange, value, stackProps, ...otherProps } = props;
  const validateSelect = useSelectValidator<T>(validationProps);
  const dispatch = useAppDispatch();

  const errorMessage = useAppSelector(GetFieldIsValidAndErrorMessage(validationProps.formId, validationProps.fieldId));
  const isValid = !errorMessage;

  // This works only if we update
  const handleOnChange = (event: SelectChangeEvent<T>, child: React.ReactNode) => {
    validateSelect(event.target.value);
    onChange && onChange(event, child);
  };

  useEffect(() => {
    return () => {
      dispatch(
        DeleteField({
          formId: validationProps.formId,
          formSectionId: validationProps.formSectionId,
          fieldId: validationProps.fieldId,
        })
      );
    };
  }, []);

  return (
    <Stack {...stackProps}>
      {label && (
        <InputLabel sx={{ mb: 1 }} htmlFor={`${validationProps.fieldId}-label`} required={validationProps.required}>
          {label}
        </InputLabel>
      )}
      <Select
        id={validationProps.fieldId}
        IconComponent={ExpandMoreRoundedIcon}
        variant="outlined"
        onChange={handleOnChange}
        error={!isValid}
        SelectDisplayProps={
          !isValid
            ? {
                style: {
                  backgroundColor: "transparent",
                  color: "white",
                  border: "none",
                },
              }
            : {}
        }
        value={value}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        {...otherProps}
      >
        {children}
      </Select>
      {!isValid && <FormHelperText error>{errorMessage}</FormHelperText>}
    </Stack>
  );
};

export default FormInputSelect;

// TODO : try to make this working because it's cleaner than style on component
// const StyledSelect = styled<T>(Select)<SelectProps<T>>(({ error }) => ({
//   ...(error && {
//       color: "white",
//     backgroundColor: "transparent",
//     border: "none",
//   }),
// }));
